import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Target } from "lucide-react";

/** Per-site reduction target: chip when set, inline form when editing. */
export function TargetControl({ row, isAdmin, onSaved }) {
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState(row.target_pct ?? 5);
  const [notify, setNotify] = useState(row.notify !== false);
  const [busy, setBusy] = useState(false);

  const run = async (fn, message, failure) => {
    setBusy(true);
    try {
      await fn();
      toast.success(message);
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(failure, { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(
      () => api.put(`/energy/targets/${encodeURIComponent(row.site_id)}`, { target_pct: Number(pct), notify }),
      `Target saved for ${row.site_dis}`,
      "Could not save target"
    );

  const clear = () =>
    run(
      () => api.delete(`/energy/targets/${encodeURIComponent(row.site_id)}`),
      "Target removed",
      "Could not remove target"
    );

  if (!open) {
    return (
      <div className="flex items-center gap-2">
        {row.target_pct != null && (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-heading font-semibold border ${
              row.target_met ? "bg-[#F0FDF4] text-[#156F5B] border-[#BBF7D0]" : "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]"
            }`}
            data-testid={`baseline-target-chip-${row.site_id}`}
          >
            <Target className="h-3 w-3" /> −{row.target_pct}% target
            {row.notify === false ? "" : " · email on"}
          </span>
        )}
        {isAdmin && (
          <button
            onClick={() => setOpen(true)}
            data-testid={`baseline-set-target-btn-${row.site_id}`}
            className="text-[11px] font-heading font-semibold text-[#156F5B] hover:underline"
          >
            {row.target_pct != null ? "Edit target" : "Set target"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`baseline-target-form-${row.site_id}`}>
      <label className="k-sublabel">Reduce by</label>
      <input
        type="number"
        min="0"
        max="90"
        step="0.5"
        value={pct}
        onChange={(e) => setPct(e.target.value)}
        data-testid={`baseline-target-input-${row.site_id}`}
        className="k-input !w-20 !py-1 text-sm"
      />
      <span className="text-xs text-[#6B7280]">% vs baseline</span>
      <label className="flex items-center gap-1.5 text-xs text-[#4D4D4D] cursor-pointer">
        <input
          type="checkbox"
          checked={notify}
          onChange={(e) => setNotify(e.target.checked)}
          data-testid={`baseline-target-notify-${row.site_id}`}
        />
        Email me if exceeded
      </label>
      <button onClick={save} disabled={busy} data-testid={`baseline-target-save-${row.site_id}`} className="k-btn !py-1 !px-3 !text-[11px]">
        Save
      </button>
      {row.target_pct != null && (
        <button onClick={clear} disabled={busy} data-testid={`baseline-target-clear-${row.site_id}`} className="k-btn-outline !py-1 !px-3">
          Remove
        </button>
      )}
      <button onClick={() => setOpen(false)} className="text-[11px] text-[#6B7280] hover:text-[#212121]">
        Cancel
      </button>
    </div>
  );
}
